import CustomToggleButton from "@/components/Button/CustomToggleButton";
import CustomAutocomplete from "@/components/Form/InputFields/CustomAutocomplete";
import CustomSelect from "@/components/Form/InputFields/CustomSelect";
import InputFieldLabel from "@/components/Label/InputFieldLabel";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { Box, Button, Grid, IconButton, Stack, Typography } from "@mui/material";
import { useFieldArray } from "react-hook-form";
import { ConditionDateField, ConditionDayField } from "./conditionFields";
import {
  TIME_DEFAULT_VALUES,
  TimeShortcutValue,
  daysOfWeekOptions,
  getShortcutFieldValues,
  timeShortcutButtons,
} from "./data";

const moduleName = "time";
const filterName = "timeOfWeek";

export default function Time({
  control,
  errors,
  setValue,
  getValues,
  parentName,
  resetField,
}: any) {
  const requiredName = `${parentName}.${moduleName}.${filterName}`;
  const fieldArrayName = `${requiredName}.value`;
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: fieldArrayName,
  });

  function onShortcutClick(shortcut: TimeShortcutValue) {
    replace(getShortcutFieldValues(shortcut));
  }

  function onAddClick() {
    append(TIME_DEFAULT_VALUES);
  }

  function onRemoveClick(index: number) {
    remove(index);
  }

  function onResetClick() {
    replace([TIME_DEFAULT_VALUES]);
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
        overflow: "auto",
        padding: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ flex: "1 1 300px", minWidth: 0 }}>
          <CustomAutocomplete
            name={`${requiredName}.key`}
            control={control}
            itemList={
              [
                { label: "User Location Based", value: "user_time_of_week" },
                { label: "Eastern Timezone", value: "time_of_week" },
              ] as any
            }
            error={errors?.advertiserId?.message}
            label="Timezone"
            disableClearable
          />
        </Box>
        <Box sx={{ flexShrink: 0 }}>
          <CustomToggleButton
            control={control}
            name={`${requiredName}.type`}
            size="small"
            itemList={[
              { label: "include", value: "include", color: "success" },
              { label: "exclude", value: "exclude", color: "primary" },
            ]}
          />
        </Box>
      </Box>
      <Box sx={{ flexShrink: 0 }}>
        <ShortcutButton onButtonClick={onShortcutClick} onResetClick={onResetClick} />
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          overflowY: "auto",
          overflowX: "hidden",
          pr: 1,
        }}
      >
        <Grid
          container
          spacing={2}
          sx={{
            flexShrink: 0,
            mb: 1,
          }}
        >
          <Grid item xs={2.5} sx={{ textAlign: "center" }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Start Day
            </Typography>
          </Grid>
          <Grid item xs={2.5} sx={{ textAlign: "center" }}>
            <Typography variant="subtitle2" fontWeight={600}>
              End Day
            </Typography>
          </Grid>
          <Grid item xs={2.5} sx={{ textAlign: "center" }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Start Time
            </Typography>
          </Grid>
          <Grid item xs={2.5} sx={{ textAlign: "center" }}>
            <Typography variant="subtitle2" fontWeight={600}>
              End Time
            </Typography>
          </Grid>
          <Grid item xs={1.5} />
        </Grid>
        {fields.map((field, index) => {
          return (
            <Grid
              key={field.id}
              container
              spacing={2}
              sx={{ flexShrink: 0 }}
            >
              <Grid item xs={2.5}>
                <CustomSelect
                  name={`${fieldArrayName}.${index}.startDay`}
                  control={control}
                  itemList={daysOfWeekOptions}
                  error={errors?.[fieldArrayName]?.[index]?.startDay?.message}
                />
              </Grid>
              <Grid item xs={2.5}>
                <ConditionDayField
                  name={`${fieldArrayName}.${index}.endDay`}
                  control={control}
                  itemList={daysOfWeekOptions}
                  index={index}
                  fieldArrayName={fieldArrayName}
                  error={errors?.[fieldArrayName]?.[index]?.endDay?.message}
                  fieldType="endDay"
                />
              </Grid>
              <Grid item xs={2.5}>
                <ConditionDateField
                  name={`${fieldArrayName}.${index}.startTime`}
                  index={index}
                  control={control}
                  fieldArrayName={fieldArrayName}
                  error={errors?.[fieldArrayName]?.[index]?.startTime?.message}
                  fieldType="startTime"
                />
              </Grid>
              <Grid item xs={2.5}>
                <ConditionDateField
                  name={`${fieldArrayName}.${index}.endTime`}
                  index={index}
                  control={control}
                  fieldArrayName={fieldArrayName}
                  error={errors?.[fieldArrayName]?.[index]?.endTime?.message}
                  fieldType="endTime"
                />
              </Grid>
              <Grid item xs={1.5}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    height: "56px",
                    gap: 1,
                  }}
                >
                  <IconButton
                    disabled={fields.length < 2}
                    onClick={() => onRemoveClick(index)}
                    size="small"
                  >
                    <RemoveIcon />
                  </IconButton>
                  {fields.length - 1 === index && (
                    <IconButton onClick={onAddClick} size="small">
                      <AddIcon />
                    </IconButton>
                  )}
                </Box>
              </Grid>
            </Grid>
          );
        })}
      </Box>
    </Box>
  );
}

type ShortcutButtonProps = {
  onButtonClick: (value: TimeShortcutValue) => void;
  onResetClick: () => void;
};

function ShortcutButton({ onButtonClick, onResetClick }: ShortcutButtonProps) {
  function handleClick(value: TimeShortcutValue) {
    onButtonClick(value);
  }

  return (
    <>
      <Stack direction="row" spacing={1} alignItems={"center"} flexWrap="wrap">
        <InputFieldLabel label="Shortcuts:" />

        {timeShortcutButtons.map(({ label, value }, idx) => (
          <Button
            key={value + idx}
            name={value}
            variant="custom3"
            value={value}
            size="small"
            onClick={() => handleClick(value)}
          >
            {label}
          </Button>
        ))}

        <Button
          variant="outlined"
          size="small"
          startIcon={<RestartAltIcon />}
          onClick={onResetClick}
          sx={{ ml: 2 }}
        >
          Reset
        </Button>
      </Stack>
    </>
  );
}

